import React, { createContext, useContext, useEffect, useState } from "react"
import { useOverlay } from "../../gui/Overlay/OverlayProvider"
import DialogTitle from "./DialogTitle"
import GUI from "../../gui/GUI"
import Button from "../../gui/Button/Button"
import CloseIcon from "../../gui/Icons/CloseIcon"
import SaveIcon from "../../gui/Icons/SaveIcon"
import EditIcon from "../../gui/Icons/EditIcon"
import DeleteIcon from "../../gui/Icons/DeleteIcon"
import useConfirm from "../../gui/Dialogs/Confirm"
import { usePath } from "../../libs/path/PathProvider"
import { ICache } from "../../data/ICache"
import { useSnackbar } from "../../gui/Snackbar/SnackbarProvider"
import Loading from "../../gui/Loading/Loading"
import Rows from "../../jslibs/Rows"
import { IRecipe } from "../../shared/Recipes"
import { IMeal } from "../../shared/Meals"

export type TFullscreenDlgContext<T> = {
    item:T
    edit:boolean
    dirty:boolean
    valid:boolean
    update:(dirty:boolean,valid:boolean,data:any) => void
}

export default function createFullscreenContext<T>() {
    const FullscreenDlgContext = createContext<TFullscreenDlgContext<T>>({} as any)

    const useFullscreen = () => useContext(FullscreenDlgContext)

    type TFullscreenDlgProps<T> = {
        overlayId:string
        item:T
        content:JSX.Element
        cache:ICache<T>
    }

    function FullscreenDlg(props:TFullscreenDlgProps<T>) {
        const [item,setItem] = useState<T>(props.item)                          // original cache
        const [data,setData] = useState<Partial<T>|undefined>(undefined)        // local updates
        const [edit,setEdit] = useState<boolean>(false)
        const [dirty,setDirty] = useState<boolean>(false)
        const [valid,setValid] = useState<boolean>(false)
        const [shadow,setShadow] = useState<boolean>(false)
        const [busy,setBusy] = useState<boolean>(false)

        const {confirm} = useConfirm()
        const {path,setPath} = usePath()
        const {addSnackbar} = useSnackbar()
        const {popOverlay} = useOverlay();

        // call from child eidtor
        const update = (dirty:boolean,valid:boolean,data:any) => {
            setDirty(dirty)
            setValid(valid)
            setData(data)
        }
        const onClose = () => {
            popOverlay(props.overlayId)
            const [_,route] = path.split("/")
            setPath("/"+route)
        }
        const onSave = async () => {
            if (!data || busy) return
            else setBusy(true)
            try { 
               await props.cache.updateItem(props.cache.itemId(item), data) 
                setEdit(false)
            }
            catch(e){ addSnackbar(e) }
            finally{ setBusy(false) }
        }
        const onScroll = (e:React.UIEvent) => setShadow(e.currentTarget.scrollTop>0)
        const onDeleteConfirm = async (dismiss:Function) => {
            dismiss()
            try {
                await props.cache.removeItem(props.cache.itemId(item))
                onClose()
            }catch(e){
                addSnackbar(e)
            }
        }
        const onDelete = () => confirm({msg: `Are you sure you wish to delete ${props.cache.itemName(item)}?`, onYes:onDeleteConfirm}) 
        const onRefresh = (id:string, updated:Rows<string,T>) => setItem(updated.find(props.cache.itemId(item))!)
        useEffect(() => {
            props.cache.on("update", onRefresh)
            return () => { props.cache.removeListener("update", onRefresh) }
        }, [])

        let toolbar:React.ReactElement
        if (edit) {
            toolbar = <>
                <Button size="small" onClick={() => setEdit(false)}><CloseIcon/>Cancel</Button>
                <Button size="small" onClick={onSave} disabled={!valid || !dirty}><SaveIcon/> Save</Button>
            </> 
        }
        else {
            toolbar = <>
                <Button size="small" onClick={() => setEdit(true)}><EditIcon/>Edit</Button>
                <Button size="small" onClick={onDelete}><DeleteIcon/> Delete</Button>
            </>
        }

        return <FullscreenDlgContext.Provider value={{item,edit,dirty,valid,update}}>
            <DialogTitle title={ props.cache.itemName(item)} onClose={onClose}></DialogTitle>
            <div className={GUI.joinClass("content", edit ? {valid,dirty,invalid:!valid,pristine:!dirty} : "")}>
                {item ? <>
                    <div className={GUI.joinClass("toolbar", shadow ? "shadow" : "")}>{toolbar}</div>
                    <div className="scroller" onScroll={onScroll}>{props.content}</div>
                </> : <Loading className="mt-32"/>}
            </div>
        </FullscreenDlgContext.Provider>
    }

    return {FullscreenDlg, useFullscreen}
}

export const RecipeFullscreen = createFullscreenContext<IRecipe>()
export const MealsFullscreen = createFullscreenContext<IMeal>()